/**
 * Compress and resize an image file in the browser before upload.
 * Returns a new File (JPEG) ≤ maxW × maxH at the given quality.
 * Works with any input format the browser can decode (JPEG, PNG, HEIC on iOS, WebP, etc.)
 *
 * @param {File}   file     – the original file from <input type="file">
 * @param {number} maxW     – max width in px  (default 1200)
 * @param {number} maxH     – max height in px (default 1200)
 * @param {number} quality  – JPEG quality 0–1 (default 0.82)
 * @returns {Promise<File>}
 */
export function compressImage(file, maxW = 1200, maxH = 1200, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Cannot decode image')); };

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down proportionally if needed
      if (width > maxW || height > maxH) {
        const ratio = Math.min(maxW / width, maxH / height);
        width  = Math.round(width  * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';          // white background — prevents black on transparent PNGs
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (!blob) return reject(new Error('Compression failed'));
          // Give compressed file a .jpg extension regardless of source format
          const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality
      );
    };

    img.src = url;
  });
}
